import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { EmptyState, PageHeader, Panel } from "../../components/Page";
import StatusBadge from "../../components/StatusBadge";
import { apiUrl, getPayload } from "../../services/apiConfig";
import { errorMessage, formatDateTime, formatDuration, methodClass } from "../../utils/formatters";

export default function ExecucoesPage() {
  const [execucoes, setExecucoes] = useState([]);
  const [filter, setFilter] = useState("");
  const [limite, setLimite] = useState(100);
  const [erro, setErro] = useState("");

  const carregar = useCallback(() => {
    setErro("");
    axios
      .get(apiUrl("/execucoes/recentes"), { params: { limite } })
      .then((response) => setExecucoes(getPayload(response) || []))
      .catch((error) => setErro(errorMessage(error, "Não foi possível carregar o histórico.")));
  }, [limite]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  useEffect(() => {
    if (!execucoes.some((execucao) => execucao.status === "EmExecucao")) return undefined;

    const interval = window.setInterval(carregar, 3000);
    return () => window.clearInterval(interval);
  }, [carregar, execucoes]);

  const filtered = useMemo(() => {
    const term = filter.trim().toLowerCase();
    if (!term) return execucoes;
    return execucoes.filter((execucao) =>
      [execucao.nomeJob, execucao.url, execucao.status, execucao.origem, execucao.codigoHttp, execucao.mensagemErro, execucao.resumoResposta]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term))
    );
  }, [execucoes, filter]);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Histórico de execuções"
        subtitle="Últimas chamadas registradas no banco Historico."
        actions={
          <button type="button" onClick={carregar} className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50">
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.02 9.35h4.5v-4.5M7.98 14.65h-4.5v4.5M20.1 9A8.25 8.25 0 0 0 6.2 5.3L3.48 8m0 7A8.25 8.25 0 0 0 17.8 18.7l2.72-2.7" />
            </svg>
            Atualizar
          </button>
        }
      />

      {erro && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{erro}</div>}

      <Panel
        actions={
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
            <input value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="Buscar execução" className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100 sm:w-80" />
            <select value={limite} onChange={(event) => setLimite(Number(event.target.value))} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100">
              <option value={50}>50 registros</option>
              <option value={100}>100 registros</option>
              <option value={250}>250 registros</option>
            </select>
          </div>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1080px] text-left">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Job</th>
                <th className="px-4 py-3">Método</th>
                <th className="px-4 py-3">Origem</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Início</th>
                <th className="px-4 py-3">Duração</th>
                <th className="px-4 py-3">HTTP</th>
                <th className="px-4 py-3">Mensagem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((execucao) => (
                <tr key={execucao.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <p className="max-w-sm truncate text-sm font-semibold text-slate-900">{execucao.nomeJob}</p>
                    <p className="max-w-sm truncate text-xs text-slate-500">{execucao.url}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full border px-2 py-0.5 text-xs font-bold ${methodClass(execucao.metodoHttp)}`}>{execucao.metodoHttp}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-700">{execucao.origem}</td>
                  <td className="px-4 py-3"><StatusBadge value={execucao.status} /></td>
                  <td className="px-4 py-3 text-sm text-slate-700">{formatDateTime(execucao.iniciadaEm)}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{formatDuration(execucao.duracaoMs)}</td>
                  <td className="px-4 py-3 text-sm text-slate-700">{execucao.codigoHttp ?? "-"}</td>
                  <td className="max-w-sm px-4 py-3 text-sm text-slate-600">
                    <span className="line-clamp-2">{execucao.mensagemErro || execucao.resumoResposta || "-"}</span>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8}>
                    <EmptyState title="Nenhuma execução encontrada." />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
